import { useState } from 'react';
import { cn } from '@/lib/utils';

export interface RatingItem {
  value: number;
  label?: string;
}

interface RatingProps {
  ratings: RatingItem[];
  name?: string;
  value?: number;
  defaultValue?: number;
  onChange?: (value: number) => void;
  className?: string;
  starSize?: number;
  allowDeselect?: boolean;
  filledColor?: string;
  emptyColor?: string;
}

export function getNextRatingValue({
  allowDeselect,
  currentValue,
  selectedValue,
}: {
  allowDeselect: boolean;
  currentValue: number;
  selectedValue: number;
}): number {
  return allowDeselect && currentValue === selectedValue ? 0 : selectedValue;
}

// oxlint-disable-next-line complexity
export function Rating({
  ratings,
  name,
  value: controlledValue,
  defaultValue,
  onChange,
  className,
  starSize = 40,
  allowDeselect = false,
  filledColor = '#3b82f6',
  emptyColor = '#d1d5db',
}: RatingProps) {
  const [internalValue, setInternalValue] = useState<number>(defaultValue ?? 0);
  const [hovered, setHovered] = useState<number | null>(null);

  const isControlled = controlledValue !== undefined;
  const value = isControlled ? controlledValue : internalValue;

  const handleSelect = (rating: number) => {
    const nextValue = getNextRatingValue({
      allowDeselect,
      currentValue: value,
      selectedValue: rating,
    });

    if (!isControlled) {
      setInternalValue(nextValue);
    }

    onChange?.(nextValue);
  };

  const activeValue = hovered ?? value;
  const activeLabel = ratings.find((r) => r.value === value)?.label;

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      {name && <input type="hidden" name={name} value={value} />}
      <div
        className="flex flex-row gap-2"
        role="radiogroup"
        aria-label="Bewertung">
        {ratings.map((rating) => {
          const isFilled = rating.value <= activeValue;
          return (
            <button
              key={rating.value}
              type="button"
              role="radio"
              aria-checked={value === rating.value}
              aria-label={
                rating.label
                  ? `${rating.value} Stern${rating.value !== 1 ? 'e' : ''}: ${rating.label}`
                  : `${rating.value} Stern${rating.value !== 1 ? 'e' : ''}`
              }
              onClick={() => {
                setHovered(null);
                handleSelect(rating.value);
              }}
              // Touch taps emit pointerenter without a matching leave, which
              // would keep the hover preview stuck and hide a deselect.
              onPointerEnter={(e) => {
                if (e.pointerType === 'mouse') {
                  setHovered(rating.value);
                }
              }}
              onPointerLeave={() => {
                setHovered(null);
              }}
              className="cursor-pointer rounded p-0.5 transition-transform hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
              <StarIcon
                size={starSize}
                color={isFilled ? filledColor : emptyColor}
                filled={isFilled}
              />
              <span className="sr-only">
                {rating.value} {rating.value === 1 ? 'Stern' : 'Sterne'}
              </span>
            </button>
          );
        })}
      </div>

      <div className="min-h-4">
        {activeLabel && (
          <p className="text-neutrals-700 text-sm leading-relaxed">
            {activeLabel}
          </p>
        )}
      </div>
    </div>
  );
}

function StarIcon({
  size,
  color,
  filled,
}: {
  size: number;
  color: string;
  filled: boolean;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={filled ? color : 'none'}
      stroke={color}
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}
